
import React, { useState } from "react";
import { KeyboardArrowDown, ExitToApp as SignOutIcon } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  IconButton,
  useMediaQuery,
  Drawer,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MenuIcon from "@mui/icons-material/Menu";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { revertAll } from "../../../app/revertAll";
import footerLogo from "../../../components/assets/footer-logo.svg";
import { setActiveSidebar } from "../../../toolkit/sidebar/sidebarSlice";
import { useLogoutMutation } from "../../../toolkit/signin/signinApi";
import { routes } from "../../../utils/constants/routes";
// import { SellerSidebarOptions } from "../../../utils/constants/sidebar";
import {
  navListContainer,
  navListItem,
  sidebarButton,
  sidebarButtonIcon,
  sidebarButtonText,
  sidebarContainer,
  sidebarFooter,
} from "./SidebarSx";
import theme from "../../../theme";
import { reducerPaths } from "../../../app/reducerPaths";
import { USER_ROLES } from "../../../utils/constants/users/userRoles";
import { exportAuction, localAuction } from "../../../utils/constants/auction";
import { sidebarWidthInPx } from "../../../utils/constants/sidebar";

const Sidebar = ({
  sidebarOptions
}) => {
  // useNavigate hook...
  const navigate = useNavigate();
  // useNavigate hook...

  const dispatch = useDispatch();
  const auth = useSelector(store => store[reducerPaths.auth]);
  const { activeSidebar } = useSelector((store) => store[reducerPaths.sidebar]);
  const [logoutAction] = useLogoutMutation();

  // Multiple breakpoints for better responsiveness
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTablet = useMediaQuery("(max-width: 900px)");
  const isLaptop = useMediaQuery("(max-width: 1200px)");
  
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const { t } = useTranslation('sidebar');

  // Function to get translated label for sidebar item
  const getTranslatedLabel = (item) => {
    // Try to get translation using the item's value as key
    const translatedLabel = t(item.value, { defaultValue: item.label });
    return translatedLabel !== item.value ? translatedLabel : item.label;
  };

  // Function to get translated group label
  const getTranslatedGroupLabel = (groupLabel) => {
    // Convert groupLabel to camelCase or use as is
    const groupKey = groupLabel.replace(/\s+/g, '').toLowerCase();
    const translatedGroupLabel = t(groupKey, { defaultValue: groupLabel });
    return translatedGroupLabel !== groupKey ? translatedGroupLabel : groupLabel;
  };

  // Handlers start...
  const getSidebarGroupInArr = (groupLabel) => {
    const result = [];

    sidebarOptions.forEach(option => {
      if (option.groupLabel === groupLabel) {
        result.push(option);
      }
    });

    return result;
  }

  const isGroupAlreadyProcessed = (groupLabel, index) => {
    return sidebarOptions.find((option, optionIdx) => {
      if (option.groupLabel === groupLabel && optionIdx > index) {
        return true;
      }
    });
  }
  // Handlers end...

  const handleNavigation = (item) => {
    dispatch(setActiveSidebar(item));
    navigate(`${routes.dashboardNavigation}${item?.navigationRoute}`);
    // Close drawer on mobile/tablet after navigation
    if (isTablet) {
      setDrawerOpen(false);
    }
  };

  const handleLogout = () => {
    logoutAction({}).then((res) => {
      if (res?.error) {
        console.error("Logout failed:", res.error);
      } else {
        console.log("Logout successful:", res.data);
      }
    });
    dispatch(revertAll());
    navigate(routes.authSignin);
    
    // Close drawer on mobile/tablet after logout
    if (isTablet) {
      setDrawerOpen(false);
    }
  };

  // Sidebar content component
  const sidebarContent = (
    <Paper 
      elevation={3} 
      sx={{
        // ...sidebarContainer,
        ...sidebarContainer({
          auth
        }),
        height: '100vh',
        width: '100%',
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ paddingTop: isTablet ? '70px' : '0' }}>
        <Box sx={{ 
          // padding: '24px', 
          textAlign: 'center',
          borderBottom: isTablet ? '1px solid rgba(255,255,255,0.1)' : 'none'
        }}>
          <img 
            src={footerLogo} 
            alt="Logo" 
            style={{ 
              maxWidth: "100%", 
              height: "auto",
              display: 'block',
              margin: '0 auto'
            }} 
          />
        </Box>

        <List sx={navListContainer}>
          {sidebarOptions.map((item, itemIdx) => {
            if (auth?.user_role === USER_ROLES.admin && auth?.auction_type === exportAuction && 
              (item.value === "sellers" || item?.value === "dealers")
            ) {
              return;
            } else if (auth?.user_role === USER_ROLES.admin && auth?.auction_type === localAuction && item.value === "buyers") {
              return;
            }

            if (item.dontShow) {
              return null; // Skip rendering this item
            }

            if (item.group && !isGroupAlreadyProcessed(item.groupLabel, itemIdx)) {
              return (
                <Accordion 
                  key={item.groupLabel}
                  className="accordion"
                  sx={{
                    background: "transparent",
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<KeyboardArrowDown sx={{
                      color: theme.palette.background.paper
                    }} />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    className="accordion-summary"
                  >
                    <Typography
                      sx={{
                        color: theme.palette.background.paper
                      }}
                    >
                      {getTranslatedGroupLabel(item.groupLabel)}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{ overflow: "hidden" }}
                    className="accordion-details"
                  >
                    <Box>
                      {getSidebarGroupInArr(item.groupLabel)?.map?.((subItem) => {
                        return (
                          <ListItem
                            key={subItem.value}
                            button
                            sx={{
                              ...navListItem(subItem.value === activeSidebar.value),
                            }}
                            onClick={() => handleNavigation(subItem)}
                          >
                            <ListItemText
                              primary={
                                <Typography
                                  color={
                                    subItem.value === activeSidebar.value
                                      ? "primary.main"
                                      : "text.secondary"
                                  }
                                >
                                  {getTranslatedLabel(subItem)}
                                </Typography>
                              }
                            />
                          </ListItem>
                        )
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )
            }

            if (!item.group) {
              return (
                <ListItem
                  key={item.value}
                  button
                  sx={navListItem(item.value === activeSidebar.value)}
                  onClick={() => handleNavigation(item)}
                >
                  <ListItemText
                    primary={
                      <Typography
                        color={
                          item.value === activeSidebar.value
                            ? "primary.main"
                            : "text.secondary"
                        }
                      >
                        {getTranslatedLabel(item)}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            }
          })}
        </List>
      </Box>

      {/* Spacer to push Sign Out to bottom */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Sign Out button at bottom */}
      <Box sx={sidebarFooter}>
        <Button
          button
          sx={sidebarButton}
          onClick={handleLogout}
        >
          <ListItemIcon sx={sidebarButtonIcon}>
            <SignOutIcon />
          </ListItemIcon>
          <Typography sx={sidebarButtonText}>
            {t('signOut')}
          </Typography>
        </Button>
      </Box>
    </Paper>
  );

  return (
    <>
      {/* Show menu button for tablets and mobile */}
      {isTablet && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{ 
            position: "fixed", 
            top: 16, 
            left: 16, 
            zIndex: 1301,
            backgroundColor: 'primary.main',
            color: 'white',
            width: 48,
            height: 48,
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* For mobile and tablet - use drawer */}
      {isTablet ? (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          sx={{ 
            zIndex: 1300,
            '& .MuiDrawer-paper': {
              width: isMobile ? '85vw' : '300px',
              maxWidth: '300px',
              border: 'none',
              padding: 0,
              margin: 0,
              overflow: 'hidden',
            },
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            }
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        /* For desktop and laptop - show sidebar directly */
        <Box sx={{ 
          position: 'fixed',
          left: 0,
          top: 0,
          height: '100vh',
          zIndex: 1200,
          width: `${sidebarWidthInPx}px`
          // width: isLaptop ? '250px' : '280px', // Slightly smaller on laptop
        }}>
          {sidebarContent}
        </Box>
      )}

      {/* Add margin spacer for main content on desktop/laptop */}
      {!isTablet && (
        <Box sx={{ 
          width: `${sidebarWidthInPx}px`,
          // width: isLaptop ? '250px' : '280px',
          flexShrink: 0,
          transition: 'width 0.3s ease',
        }} />
      )}
    </>
  );
};

export default Sidebar;